/**
 * InputField.tsx
 * ------------------------------------------------------------------
 * Production-ready animated input component
 * - Strict TypeScript typing
 * - Accessible (ARIA-compliant)
 * - Motion-based micro-interactions
 * - Theme-aware (light / dark)
 * - Extensible & maintainable
 * ------------------------------------------------------------------
 */

import {
  motion,
  useAnimation,
  useInView,
  AnimatePresence,
  Variants,
} from "motion/react";
import React, {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/* ------------------------------------------------------------------
 * Types
 * ------------------------------------------------------------------ */

export interface InputFieldProps {
  id?: string;
  name?: string;
  type?: React.HTMLInputTypeAttribute;
  value?: string;
  min?: string;
  max?: string;
  checked?: boolean;

  label?: string;
  placeholder?: string;
  icon?: React.ReactNode;
  endAdornment?: React.ReactNode;

  error?: string;
  touched?: boolean;
  disabled?: boolean;

  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;

  inputMode?:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";

  accept?: string;
  autoComplete?: string;
  ariaLabel?: string;

  className?: string;
  color?: string;
  theme?: "light" | "dark";
}

/* ------------------------------------------------------------------
 * Motion Variants
 * ------------------------------------------------------------------ */

const labelVariants: Variants = {
  hidden: { opacity: 0, x: -8 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3 },
  },
};

const containerVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 120, damping: 14 },
  },
};

const errorVariants: Variants = {
  hidden: { opacity: 0, height: 0 },
  visible: {
    opacity: 1,
    height: "auto",
    transition: { duration: 0.25 },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.2 },
  },
};

/* ------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------ */

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      id,
      name,
      type = "text",
      value,
      min,
      max,
      checked,

      label,
      placeholder,
      icon,
      endAdornment,

      error,
      touched,
      disabled,

      onChange,
      onBlur,
      onKeyDown,

      inputMode,
      accept,
      autoComplete,
      ariaLabel,

      className = "",
      color = "#3b82f6",
      theme = "light",
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(containerRef, { once: true, amount: 0.25 });
    const controls = useAnimation();
    const [focused, setFocused] = useState(false);

    /* ------------------------------------------------------------------
     * Effects
     * ------------------------------------------------------------------ */

    useEffect(() => {
      if (isInView) {
        controls.start("visible");
      }
    }, [isInView, controls]);

    /* ------------------------------------------------------------------
     * Theme & Class Computation
     * ------------------------------------------------------------------ */

    const isDark = theme === "dark";

    const baseInputClasses = useMemo(
      () =>
        [
          "w-full rounded-xl border outline-none transition-all duration-200",
          "py-3.5 text-base md:text-sm",
          icon ? "pl-11 md:pl-12" : "pl-4",
          endAdornment ? "pr-12 md:pr-14" : "pr-4",
          disabled ? "opacity-60 cursor-not-allowed" : "",
        ].join(" "),
      [icon, endAdornment, disabled]
    );

    const themeClasses = useMemo(() => {
      if (isDark) {
        return error
          ? "bg-neutral-800 text-white border-red-500 placeholder:text-gray-400"
          : "bg-neutral-800 text-white border-gray-700 focus:border-blue-500 placeholder:text-gray-400";
      }

      return error
        ? "bg-gray-100 text-neutral-900 border-red-500 placeholder:text-gray-500"
        : "bg-gray-100 text-neutral-900 border-gray-300 focus:border-blue-500 placeholder:text-gray-500";
    }, [isDark, error]);

    /* ------------------------------------------------------------------
     * Render
     * ------------------------------------------------------------------ */

    return (
      <div ref={containerRef} className="w-full space-y-2">
        {label && (
          <motion.label
            htmlFor={id}
            className={`block text-sm font-medium ${
              isDark ? "text-gray-300" : "text-neutral-800"
            }`}
            variants={labelVariants}
            initial="hidden"
            animate={controls}
          >
            {label}
          </motion.label>
        )}

        <motion.div
          className="relative w-full"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {icon && (
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              {icon}
            </div>
          )}

          <motion.input
            ref={ref}
            id={id}
            name={name}
            type={type}
            value={value}
            min={min}
            max={max}
            checked={checked}
            disabled={disabled}
            placeholder={placeholder}
            inputMode={inputMode}
            accept={accept}
            autoComplete={autoComplete}
            aria-label={ariaLabel || label}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${id}-error` : undefined}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={(e) => {
              setFocused(false);
              onBlur?.(e);
            }}
            className={`${baseInputClasses} ${themeClasses} ${className}`}
            style={{
              borderColor:
                !error && value && value.length > 0 ? color : undefined,
              boxShadow: focused
                ? isDark
                  ? "0 0 0 3px rgba(59,130,246,0.35)"
                  : "0 0 0 3px rgba(59,130,246,0.25)"
                : error
                ? "0 0 0 2px rgba(239,68,68,0.15)"
                : undefined,
            }}
          />

          {endAdornment && (
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
              {endAdornment}
            </div>
          )}

          {/* Focus glow overlay */}
          <AnimatePresence>
            {focused && !disabled && (
              <motion.div
                className="absolute inset-0 rounded-xl pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.08 }}
                exit={{ opacity: 0 }}
                style={{
                  backgroundColor: color,
                  boxShadow: `0 0 12px ${color}55`,
                }}
              />
            )}
          </AnimatePresence>
        </motion.div>

        {/* Error message */}
        <AnimatePresence>
          {error && touched && (
            <motion.p
              id={`${id}-error`}
              role="alert"
              className="ml-1 text-sm text-red-500"
              variants={errorVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

InputField.displayName = "InputField";

export default InputField;
